import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    UserCircle,
    CreditCard,
    LogOut,
    Loader2,
} from "lucide-react";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { AppShell } from "@/components/reelflo/AppShell";
import {
    GoogleConnectDialog,
    GoogleG,
} from "@/components/reelflo/GoogleConnectDialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CARD =
    "flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6";
const ACTION_BTN =
    "inline-flex flex-none items-center justify-center gap-2 rounded-full border border-white/15 min-h-11 px-4 text-sm font-semibold text-white/80 transition-colors hover:border-purple-400/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-60";

// Labeled account row: an accent icon tile, a title + subtitle, and a
// right-aligned status badge and/or action control.
const SettingCard = ({ testId, iconEl, accent, title, subtitle, right }) => (
    <section data-testid={testId} className={CARD}>
        <div className="flex items-center gap-4 sm:gap-5">
            <div
                className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border sm:h-14 sm:w-14"
                style={{
                    borderColor: `${accent}66`,
                    background: `${accent}1f`,
                }}
            >
                {iconEl}
            </div>
            <div className="min-w-0">
                <p className="rf-heading text-xl font-bold leading-tight text-white sm:text-2xl">
                    {title}
                </p>
                <p className="mt-1 text-sm text-white/55 sm:text-base">
                    {subtitle}
                </p>
            </div>
        </div>
        <div className="flex items-center gap-3 sm:flex-none">{right}</div>
    </section>
);

export default function Account() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [googleOpen, setGoogleOpen] = useState(false);
    const [billingLoading, setBillingLoading] = useState(false);

    const googleConnected = !!user?.google_connected;
    const subActive = ["active", "trialing"].includes(user?.subscription_status);

    const goTo = (key) =>
        navigate(
            key === "videos"
                ? "/videos"
                : key === "account"
                  ? "/account"
                  : "/dashboard",
        );

    const doLogout = () => {
        logout();
        navigate("/");
    };

    // Hand off to Stripe's hosted customer portal. The returned URL is
    // short-lived sensitive data: keep it in a LOCAL variable only and navigate
    // immediately. It is never written to state, storage, history, or logs.
    const handleManageBilling = async () => {
        setBillingLoading(true);
        try {
            const { data } = await api.post("/account/billing/portal");
            const url = data?.url;
            if (url) {
                window.location.href = url;
                return;
            }
            toast.error("Could not open the billing portal. Please try again.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not open the billing portal. Please try again.",
            );
        } finally {
            setBillingLoading(false);
        }
    };

    return (
        <div
            data-testid="account-page"
            className="relative min-h-screen overflow-hidden bg-black text-white"
        >
            <NeonBackground intensity={0.4} />

            <AppShell
                active="account"
                onLogoClick={() => navigate("/")}
                onNavigate={goTo}
            >

            <main className="relative z-10 mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
                <div className="mb-8">
                    <h1 className="rf-display text-4xl uppercase sm:text-5xl">
                        Account
                    </h1>
                    <p className="mt-3 text-lg text-white/55">
                        Manage your profile, connected apps, and billing.
                    </p>
                </div>

                <div className="flex flex-col gap-5">
                    {/* Profile */}
                    <section
                        data-testid="account-profile-card"
                        className="flex flex-col gap-4 rounded-lg border border-white/12 bg-surface-panel p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
                    >
                        <div className="flex items-center gap-4 sm:gap-5">
                            <div
                                className="flex h-14 w-14 flex-none items-center justify-center rounded-lg border"
                                style={{
                                    borderColor: "#A855F766",
                                    background: "#A855F71f",
                                }}
                            >
                                <UserCircle className="h-7 w-7 text-purple-300" />
                            </div>
                            <div className="min-w-0">
                                <p
                                    data-testid="account-name"
                                    className="rf-heading truncate text-2xl font-bold leading-tight text-white"
                                >
                                    {user?.full_name || "Your account"}
                                </p>
                                <p
                                    data-testid="account-email"
                                    className="mt-1 truncate text-base text-white/55"
                                >
                                    {user?.email}
                                </p>
                            </div>
                        </div>
                        <span
                            data-testid="account-subscription-status"
                            className={[
                                "inline-flex flex-none items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold",
                                subActive
                                    ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                                    : "border-white/15 bg-white/[0.04] text-white/60",
                            ].join(" ")}
                        >
                            <span
                                className={[
                                    "h-2.5 w-2.5 rounded-full",
                                    subActive ? "bg-emerald-300" : "bg-white/40",
                                ].join(" ")}
                            />
                            {subActive
                                ? "Active subscription"
                                : "No active subscription"}
                        </span>
                    </section>

                    {/* Google */}
                    <SettingCard
                        testId="account-google-section"
                        accent="#4285F4"
                        iconEl={<GoogleG className="h-6 w-6 sm:h-7 sm:w-7" />}
                        title="Google"
                        subtitle="Connect Google Docs & Drive to pull in your content."
                        right={
                            <>
                                <span
                                    data-testid="account-google-status"
                                    className={[
                                        "hidden text-sm font-semibold sm:inline",
                                        googleConnected
                                            ? "text-emerald-300"
                                            : "text-white/45",
                                    ].join(" ")}
                                >
                                    {googleConnected
                                        ? "Connected"
                                        : "Not connected"}
                                </span>
                                <button
                                    type="button"
                                    data-testid="account-google-manage-button"
                                    onClick={() => setGoogleOpen(true)}
                                    className={ACTION_BTN}
                                >
                                    {googleConnected ? "Manage" : "Connect"}
                                </button>
                            </>
                        }
                    />

                    {/* Billing */}
                    <SettingCard
                        testId="account-billing-section"
                        accent="#22D3EE"
                        iconEl={
                            <CreditCard className="h-6 w-6 text-cyan-300 sm:h-7 sm:w-7" />
                        }
                        title="Billing"
                        subtitle="Update your payment method, view invoices, or cancel."
                        right={
                            <button
                                type="button"
                                data-testid="account-manage-billing-button"
                                onClick={handleManageBilling}
                                disabled={billingLoading}
                                className={ACTION_BTN}
                            >
                                {billingLoading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <CreditCard className="h-4 w-4" />
                                )}
                                Manage billing
                            </button>
                        }
                    />

                    {/* Sign out */}
                    <button
                        type="button"
                        data-testid="account-logout-button"
                        onClick={doLogout}
                        className="mt-2 inline-flex items-center justify-center gap-2 self-start rounded-full border border-rose-400/30 bg-rose-500/10 px-5 py-3 text-sm font-semibold text-rose-200 transition-colors hover:border-rose-300/50 hover:bg-rose-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50"
                    >
                        <LogOut className="h-4 w-4" />
                        Sign out
                    </button>
                </div>
            </main>
            </AppShell>

            <GoogleConnectDialog open={googleOpen} onOpenChange={setGoogleOpen} />
        </div>
    );
}
